import { Annotation, PlottableModel, Transform, TransformGroup,TransformType } from './4d-objects';
import { Observable, of } from 'rxjs';
import { VasatModelClass, Vasat, VasatConfig } from 'vasat';
import { tap, flatMap } from 'rxjs/operators';
import { PlottableReference, Renderer } from './renderer';



/**
 * <p>Interface for the helper classes. Currently there are three maps that are supported (<a href="https://cesiumjs.org/tutorials/">Cesium</a>, <a href="https://developers.google.com/maps/documentation/javascript/tutorial">Google</a>, <a href="https://leafletjs.com/">Leaflet</a>).</p>
 */
export class MapHelper {

	protected plotCache:{[tid:string]:PlottableReference} = {}
	protected renderers:{[className:string]:
		{[filter:string]:Renderer<any>}} = {}
	
	_debug_1:string = 'color:#fff;background-color:orange;'
	_debug_2:string = 'color:#fff;background-color:#666;'
	_log( id:string = 'constructor', data:any = null ) {
		return; // if ( id == 'constructor' ) return; // hide constructor logging for now
		console.log ( '%c '+this.constructor.name+' %c '+id+' ', this._debug_1, this._debug_2, data ? data : '' );
	}
	
	/**
	 * <p>Removes all models/images from the map.</p>
	 */
	clearPlottables(){
		throw new Error("Not Implemented")
	}

	/**
	 * <p>Adds specified model/images to the map.</p>
	 * <p>See [[Transform]] for details on compatibility.</p>
	 * @param t Accepts [[Transform]] and [[Annotation]] objects.
	 * @param args 
	 * @deprecated use setPlottable
	 */
	addPlottable(t: PlottableModel<any>, args?: any): Observable<PlottableReference>{
		return this.setPlottable(t,args)
	}
	/**
	 * <p>Adds or Updates specified model/images to the map.</p>
	 * <p>See [[Transform]] for details on compatibility.</p>
	 * @param t Accepts [[Transform]] and [[Annotation]] objects.
	 * @param args 
	 */
	setPlottable(p: PlottableModel<any>, args?: any): Observable<PlottableReference>{
		var key = '_' + p.className() + '_' + p.id
		var ref = this.plotCache[key]
		var render = this.getRenderer(p)
		// console.log('##plot',render);
		if(!ref){
			return render.onAdd(p,args).pipe(
				tap(r => 
					this.plotCache[key] = r
				),
				flatMap(rr => 
					render.onUpdate(p,rr,args)
				)
			)
		}
		else
			return render.onUpdate(p,ref,args)
	
	}

	/**
	 * <p>Removes specified model object from map.</p>
	 * @param t The [[PlottableModel]] object to be removed.
	 */
	removePlottable(p: PlottableModel<any>){
		var key = '_' + p.className() + '_' + p.id
		var ref = this.plotCache[key]
		var render = this.getRenderer(p)
		if(ref){
			delete this.plotCache[key]
			return render.onRemove(p,ref)
		}
	}

	/**
	 * <p>Pans the camera to specified model object on the map.</p>
	 * @param t The [[PlottableModel]] object that you wish to pan the camera to.
	 * @param duration Time taken to pan camera to destination in milliseconds.
	 * @param args
	 */
	sendCameraTo(t: PlottableModel<any>, duration?: number, args?: any): Observable<any>{
		throw new Error("Not Implemented")
	}

	/**
	 * <p>Returns the respective Reference object of a given [[PlottableModel]] object that was plotted on the map.</p>
	 * @param p [[PlottableModel]] object
	 */
	getMapObjectFor(p: PlottableModel<any>): PlottableReference{
		return this.plotCache['_' + p.className() + '_' + p.id]
	}

	/**
	 * <p>Returns the authenticated url of the image/model of selected [[PlottableModel]].</p>
	 * @param p The [[PlottableModel]] object
	 * @param file Name of file.
	 * @param tier Applicable for Cesium only. See [[FourdCesiumHelper.getAuthenticatedUrl]] for more details.
	 */
	getAuthenticatedUrl(p: PlottableModel<any>, file: string, tier: number): Promise<any>{
		throw new Error("Not Implemented")
	}


	/**
	 * Change the transparency for a transform being displayed on this map. Transform must have been added using addPlottable
	 * @param t the transform object
	 * @param opacity zero - 1 as percent
	 */
	changeOpacity(t: Transform, opacity: number){
		throw new Error("Not Implemented")
	}

	/**
	 * use whatever this implementations click event is to resolve the item currently clicked
	 */
	resolveItem(clickEvent:any):PlottableModel<any>{
		throw new Error("Not Implemented")
	}


	registerRenderer(clazz:VasatModelClass<Annotation|Transform>, category:string|TransformType, renderer:Renderer<Annotation|Transform>){


		var cn = this.classNameForType(clazz)
		var cat = category instanceof TransformType ? category.uid:category
		
		if(!this.renderers[cn])
			this.renderers[cn] = {}
		
		this.renderers[cn][cat] = renderer;
		this._log('registerRenderer',[category,clazz,cn,cat]);
	}


	destroy(){
		Object.keys(this.plotCache).forEach(k => this.removePlottable(this.plotCache[k].plottable))
		Object.keys(this.renderers).forEach(k => 
			Object.keys(this.renderers[k])
				.forEach(kk => this.renderers[k][kk].destory()
			)
		)
		
	}

	private classNameForType(obj:VasatModelClass<any>):string{
        var t = new obj(new Vasat(new VasatConfig("","",""),null))
        return t.className()
	}
	
	private getRenderer(p:PlottableModel<any>):Renderer<any> {
		if(!this.renderers[p.className()]) throw new Error("No render for class " + p.className())

		var typeKey = "*"
		if(p instanceof Annotation)
			typeKey = p.annotationMode || typeKey
		
		else if(p instanceof Transform)
			typeKey = (p.transformType.uid) || typeKey
		
		var r:Renderer<any> = null
		r = this.renderers[p.className()][typeKey] || this.renderers[p.className()]['*']

		if(!r) throw Error("No renderer for type " + p.transformType.id)

		return r
	}
	
}